import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SimpleScenarioForm from '../../../components/ScenarioManagement/SimpleScenarioForm';
import { Scenario, ScenarioFormData } from '../../../types/ScenarioManagement';
import { JSONPersistenceService } from '../../../services/JSONPersistenceService';
import { toast } from 'react-hot-toast';

const ScenarioEditor: React.FC = () => {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // const { updateScenario } = useScenario(id || ''); // Removed - will use JSONPersistenceService directly
  
  const updateScenario = async (scenarioId: string, updatedData: Scenario) => {
    const persistence = JSONPersistenceService.getInstance();
    const data = await persistence.loadData();
    if (!data) throw new Error('No data found');
    
    const scenarioMap = new Map(data.scenarios);
    scenarioMap.set(scenarioId, updatedData as any);
    
    await persistence.saveData({
      ...data,
      scenarios: Array.from(scenarioMap.entries())
    });
  };
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load scenario data or initialize for creation
  useEffect(() => {
    const loadScenario = async () => {
      // If no scenarioId, we're in creation mode
      if (!scenarioId) {
        setScenario(null); // null indicates creation mode
        setIsLoading(false);
        return;
      }

      // Check if scenario was passed via route state
      const stateScenario = location.state?.scenario as Scenario | undefined;
      if (stateScenario && stateScenario.id === scenarioId) {
        console.log('Using scenario from route state');
        setScenario(stateScenario);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const persistence = JSONPersistenceService.getInstance();
        let data = await persistence.loadData();
        
        console.log('Looking for scenario ID:', scenarioId);
        console.log('Available scenarios:', data?.scenarios?.map(([sid]) => sid));
        
        const foundScenario = data?.scenarios.find(([sid]) => sid === scenarioId)?.[1] as any;
        
        if (!foundScenario) {
          console.error('Scenario not found. ID:', scenarioId);
          console.error('Available IDs:', data?.scenarios?.map(([sid]) => sid));
          toast.error(`Scenario '${scenarioId}' not found. Please go back to Manage Scenarios.`);
          navigate('/manage');
          return;
        }

        setScenario(foundScenario);
      } catch (error) {
        console.error('Failed to load scenario:', error);
        toast.error('Failed to load scenario');
        navigate('/manage');
      } finally {
        setIsLoading(false);
      }
    };

    loadScenario();
  }, [scenarioId, navigate, location.state]);



  // Handle manual save (both create and update)
  const handleSave = async (data: ScenarioFormData) => {
    try {
      setIsSaving(true);
      

      if (scenarioId) {
        // Update existing scenario
        const updatedScenario: Scenario = {
          ...data,
          id: scenarioId,
          updatedAt: new Date(),
          version: (scenario?.version || 0) + 1
        } as Scenario;

        await updateScenario(scenarioId, updatedScenario);
        setScenario(updatedScenario);
        toast.success('Scenario updated successfully!');
      } else {
        // Create new scenario
        const newScenario: Scenario = {
          ...data,
          id: `scenario_${Date.now()}`,
          createdAt: new Date(),
          updatedAt: new Date(),
          version: 1,
          tags: data.tags || [],
          category: data.category || 'Business',
          playCount: 0,
          averageScore: 0,
          completionRate: 0
        } as Scenario;

        const persistence = JSONPersistenceService.getInstance();
        let currentData = await persistence.loadData();
        if (!currentData) {
          currentData = await persistence.initializeWithDefaults();
        }

        const scenarioMap = new Map(currentData.scenarios);
        scenarioMap.set(newScenario.id, newScenario);
        
        await persistence.saveData({
          ...currentData,
          scenarios: Array.from(scenarioMap.entries())
        });

        toast.success('Scenario created successfully!');
      }
      
      setHasUnsavedChanges(false);
      navigate('/manage');
    } catch (error) {
      console.error('Failed to save scenario:', error);
      toast.error(scenarioId ? 'Failed to update scenario' : 'Failed to create scenario');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel with unsaved changes warning
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }

    navigate('/manage');
  };


  // Warn about unsaved changes on navigation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-950 to-navy-900 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <svg className="animate-spin h-8 w-8 text-professional-blue-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-white text-lg">Loading scenario...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything if we're loading
  if (isLoading) {
    // Loading component already handled above
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 to-navy-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header with status indicators */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-light text-white mb-2">
                {scenarioId ? 'Edit Scenario' : 'Create New Scenario'}
              </h1>
              <p className="text-slate-300">
                {scenarioId && scenario ? (
                  <>Editing: <span className="font-medium">{scenario.title}</span></>
                ) : (
                  'Create a new cybersecurity scenario'
                )}
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Auto-save status */}
              <AnimatePresence>
                {hasUnsavedChanges && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex items-center gap-2 text-yellow-400"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    <span className="text-sm">Unsaved changes</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {isSaving && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2 text-green-400"
                >
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm">Saving...</span>
                </motion.div>
              )}

              {/* Version indicator (only for existing scenarios) */}
              {scenarioId && scenario && (
                <div className="text-sm text-slate-400">
                  Version {scenario.version || 1}
                </div>
              )}
            </div>
          </div>

          {/* Breadcrumb navigation */}
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <button
              onClick={() => navigate('/manage')}
              className="hover:text-white transition-colors"
            >
              Management
            </button>
            <span>/</span>
            <button
              onClick={() => navigate('/manage')}
              className="hover:text-white transition-colors"
            >
              Scenario Management
            </button>
            <span>/</span>
            <span className="text-white">{scenarioId ? 'Edit' : 'Create'}</span>
          </nav>
        </motion.div>

        {/* Scenario Form with enhanced features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <SimpleScenarioForm
            initialData={scenario || undefined}
            onSubmit={handleSave}
            onCancel={handleCancel}
            mode={scenarioId ? 'edit' : 'create'}
          />
        </motion.div>

        {/* Keyboard shortcuts help */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 text-center text-sm text-slate-400"
        >
          <p>
            <kbd className="px-2 py-1 bg-white/10 rounded">Ctrl</kbd> + 
            <kbd className="px-2 py-1 bg-white/10 rounded ml-1">S</kbd> to save • 
            Auto-save enabled (3s after changes)
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ScenarioEditor;